<script lang="ts">
	// Svelte
	import { createEventDispatcher, onMount, setContext } from 'svelte';
	// Svelte Stores
	import { writable } from 'svelte/store';
	// Stores
	import { format } from 'svelte-i18n';
	// Helpers
	import {
		setWizardSteps,
		updateWizardStepOnWizardItem,
		updateWizardStepOnPrevNext,
		scrollToElement,
	} from '../../../../../Lists/list-helpers';
	// Design System
	import {
		Button,
		Wizard,
		WizardItem,
		Container,
		WizardContainer,
		WizardContent,
		Modal,
	} from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import ActionStep from './Steps/ActionStep.svelte';
	import DeviceStep from './Steps/DeviceAction/DeviceStep.svelte';
	import ChangeStateStep from './Steps/DeviceAction/ChangeStateStep.svelte';
	import DelayActionStep from './Steps/DelayActionStep.svelte';
	import NotificationActionStep from './Steps/NotifyAction/NotificationActionStep.svelte';
	import GroupActionStep from './Steps/GroupAction/GroupActionStep.svelte';
	import GroupChangeStageStep from './Steps/GroupAction/GroupChangeStageStep.svelte';
	import SceneActionStep from './Steps/SceneAction/SceneActionStep.svelte';
	import FullfillSceneStep from './Steps/SceneAction/FullfillSceneStep.svelte';
	import { getDoTranslationType, SCENE_ACTIONS_WITH_DELAY_OPTION } from './helpers/do-wizard-helpers';
	import AlarmActionStep from './Steps/AlarmAction/AlarmActionStep.svelte';
	import FullfillAlarmStep from './Steps/AlarmAction/FullfillAlarmStep.svelte';
	import OrphanRecipientConfirmModal from './Steps/NotifyAction/Components/OrphanRecipientConfirmModal.svelte';
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		channelForNotificationType,
		reconcileSubUnitRecipients,
	} from 'src/components/Scenes/helpers/notification_recipients_helpers';

	export let sceneData: any = {};
	export let showWizard = false;
	export let stopConfirmationModal = false;
	export let backdropClose = false;

	const dispatch = createEventDispatcher();
	const previousButton = $format('button_back');

	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedSceneData;
	let showOrphanModal = false;
	let pendingOrphans: number[] = [];

	let mappedWizardStepsComponents = {
		1: {
			0: ActionStep,
			1: DeviceStep,
			2: ChangeStateStep,
			3: DelayActionStep,
		},
		2: {
			0: ActionStep,
			1: NotificationActionStep,
			2: DelayActionStep,
		},
		3: {
			0: ActionStep,
			1: GroupActionStep,
			2: GroupChangeStageStep,
			3: DelayActionStep,
		},
		4: {
			0: ActionStep,
			1: SceneActionStep,
			2: FullfillSceneStep,
			3: DelayActionStep,
		},
		5: {
			0: ActionStep,
			1: AlarmActionStep,
			2: FullfillAlarmStep,
			3: DelayActionStep,
		},
	};

	const wizardStepsVariants = {
		1: [
			$format('wizard_step_action', { default: 'Action' }),
			$format('wizard_step_device', { default: 'Device' }),
			$format('wizard_step_change_state', { default: 'Change state' }),
			$format('wizard_step_delay_action', { default: 'Delay action' }),
		],
		2: [
			$format('wizard_step_action', { default: 'Action' }),
			$format('wizard_step_notification_type', {
				default: 'Notification type',
			}),
			$format('wizard_step_delay_action', { default: 'Delay action' }),
		],
		3: [
			$format('wizard_step_action', { default: 'Action' }),
			$format('wizard_step_group', { default: 'Group' }),
			$format('wizard_step_change_state', { default: 'Change state' }),
			$format('wizard_step_delay_action', { default: 'Delay action' }),
		],
		4: [
			$format('wizard_step_action', { default: 'Action' }),
			$format('wizard_step_scene', { default: 'Scene' }),
			$format('wizard_step_fullfill_scene', { default: 'Fulfil scene as' }),
			$format('wizard_step_delay_action', { default: 'Delay action' }),
		],
		5: [
			$format('wizard_step_action', { default: 'Action' }),
			$format('wizard_step_alarm', { default: 'Alarm' }),
			$format('wizard_step_fullfill_alarm', { default: 'Fulfil alarm as' }),
			$format('wizard_step_delay_action', { default: 'Delay action' }),
		],
	};

	let wizardContainerRef: HTMLElement = null;
	let scrollContainerRef: HTMLElement = null;
	let wizardItemsContainerRef: HTMLElement = null;
	let wizardItems = setWizardSteps(wizardStepsVariants[sceneData?.selectedAction]);
	let activeWizardStep = wizardItems?.[0];
	let modalRef: Modal = null;
	let scrolledToView = false;
	let firstSelectedRef: HTMLElement = null;

	$: wizardItems = setWizardSteps(
		wizardStepsVariants[sceneData?.selectedAction],
		activeWizardStep.activeIndex,
	);
	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');

	let containersRefsObj = writable({
		scrollContainerRef: null,
		wizardContainerRef: null,
	});

	setContext('addActionContainersRefs', containersRefsObj);

	const handleSaveDeviceActionBased = () => {
		const { selectedDevice, deviceAction } = sceneData;
		if (import.meta.env.DEV) {
			console.log('selectedDevice', selectedDevice);
			console.log('deviceAction', deviceAction);
		}
		const { actions, delay, timer } = deviceAction;
		let split_device = selectedDevice.value.split('_');
		let _device_action = {
			device: {
				id: split_device[0],
				channel: split_device[1] || '0',
			},
			actions,
			delay,
			timer,
		};

		dispatch('save', {
			t_data: _device_action,
			s_type: 'device_action',
			// REMOVE this after code cleanup. Should get the value from DoStep.svelte and use it in save function
			do_info: sceneData.do_info,
		});
	};

	const handleSaveNotificationActionBased = () => {
		const { notificationAction } = sceneData;
		dispatch('save', {
			t_data: notificationAction,
			s_type: 'notify_action',
			// REMOVE this after code cleanup. Should get the value from DoStep.svelte and use it in save function
			do_info: sceneData.do_info,
		});
	};

	const handleSaveGroupActionBased = () => {
		const { selectedGroup, groupAction } = sceneData;
		const { actions, delay } = groupAction;
		dispatch('save', {
			t_data: {
				group: Number(selectedGroup.id),
				actions,
				delay,
			},
			s_type: 'group_action',
			// REMOVE this after code cleanup. Should get the value from DoStep.svelte and use it in save function
			do_info: sceneData.do_info,
		});
	};

	const handleSaveSceneActionBased = () => {
		const { selectedScene, sceneAction } = sceneData;
		const { option, delay } = sceneAction;
		dispatch('save', {
			t_data: { scene_id: `${selectedScene.id}`, option, delay },
			s_type: 'automation_action',
			// REMOVE this after code cleanup. Should get the value from DoStep.svelte and use it in save function
			do_info: sceneData.do_info,
		});
	};

	const handleSaveAlarmActionBased = () => {
		const { selectedScene, alarmAction } = sceneData;
		const { option, delay } = alarmAction;
		dispatch('save', {
			t_data: { scene_id: `${selectedScene.id}`, option, delay },
			s_type: 'automation_action',
			// REMOVE this after code cleanup. Should get the value from DoStep.svelte and use it in save function
			do_info: sceneData.do_info,
		});
	};

	const mappedSavedFunctions = {
		1: handleSaveDeviceActionBased,
		2: handleSaveNotificationActionBased,
		3: handleSaveGroupActionBased,
		4: handleSaveSceneActionBased,
		5: handleSaveAlarmActionBased,
	};

	/**
	 * FE-1682: detect orphan sub-unit recipients on the current notification
	 * action and surface a confirmation modal before persisting. Returns
	 * `true` when the caller should pause and wait for the modal decision.
	 */
	const interceptForOrphans = (): boolean => {
		if (sceneData.selectedAction !== 2) return false;

		const notificationAction = sceneData?.notificationAction;
		const channel = channelForNotificationType(notificationAction?.type);
		if (!channel) return false;

		const { orphans } = reconcileSubUnitRecipients(notificationAction?.toSub);
		if (orphans.length === 0) return false;

		pendingOrphans = orphans;
		showOrphanModal = true;
		return true;
	};

	const handleSave = () => {
		if (interceptForOrphans()) return;
		mappedSavedFunctions[sceneData.selectedAction]();
	};

	const onOrphanRemoveAndSave = () => {
		const channel = channelForNotificationType(sceneData?.notificationAction?.type);
		if (channel) {
			const { selected } = reconcileSubUnitRecipients(sceneData?.notificationAction?.toSub);
			sceneData.notificationAction = {
				...sceneData.notificationAction,
				toSub: selected,
			};
		}
		showOrphanModal = false;
		pendingOrphans = [];
		mappedSavedFunctions[sceneData.selectedAction]();
	};

	const onOrphanKeepAndSave = () => {
		showOrphanModal = false;
		pendingOrphans = [];
		mappedSavedFunctions[sceneData.selectedAction]();
	};

	const onOrphanCancel = () => {
		showOrphanModal = false;
		pendingOrphans = [];
	};

	//#regionstart wizard functions
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};
	//#regionend wizard functions

	const isWizardStepDisabled = (wizardItem, i) => {
		const isEditMode = sceneData.do_info.mode === 'edit';
		const { isValid } = getDoTranslationType(sceneData.selectedAction, sceneData);

		// Not disabled all on edit mode and only for the type that have translation
		if (isEditMode && isValid && sceneData.isValid) {
			return false;
		}

		// Not disabled second step. First step have default value
		if (activeWizardStep.activeIndex === 0 && i === 1) {
			return false;
		}

		// Not disabled next step when is valid
		if (sceneData.isValid && sceneData.validStepIndex + 1 === i && !wizardItem.active) {
			return false;
		}

		// Not disabled active wizard step
		if (wizardItem.active) {
			return false;
		}
		// Disable all wizard items witch are bigger than last valid step
		return wizardItem.activeIndex > sceneData.validStepIndex;
	};

	const isNextButtonDisabled = (sceneData, activeWizardStep) => {
		const isEditMode = sceneData.do_info.mode === 'edit';
		const { isValid } = getDoTranslationType(sceneData.selectedAction, sceneData);

		// Not disabled when we is not valid, but last valid index is biger than current active. Is back clicked and edit.
		if (!sceneData.isValid && activeWizardStep.activeIndex < sceneData.validStepIndex) return false;

		// Not disabled all on edit mode and only for the type that have translation
		if (isEditMode && isValid && sceneData.isValid) return false;

		// Not disabled on first step
		if (activeWizardStep.activeIndex === 0) return false;

		const selectedAction = SCENE_ACTIONS_WITH_DELAY_OPTION[sceneData.selectedAction];
		if (
			isLastStepWizard &&
			sceneData.isValid &&
			(typeof sceneData[selectedAction].delay.value === 'number' ||
				sceneData[selectedAction].delay.value >= 0)
		)
			return false;

		// Not disabled all steps before last validated
		if (sceneData.isValid && activeWizardStep.activeIndex <= sceneData.validStepIndex) {
			return false;
		}

		// Disabled next button if not condition applied
		return true;
	};

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		scrolledToView = false;
		let elementRef = null;
		let wizardStepIndex = 0;

		if (activeWizardStep.activeIndex === 1 && firstSelectedRef && !scrolledToView) {
			wizardStepIndex = 1;
			elementRef = firstSelectedRef;
		} else {
			wizardStepIndex = activeWizardStep.activeIndex;
			elementRef = null;
		}

		if (!elementRef) {
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeWizardStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	const showExitConfirmation = () => {
		if (showConfirmationModal && !stopConfirmationModal) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			dispatch('close');
		}
	};

	onMount(() => {
		clonedSceneData = deepCopy(sceneData);
	});

	$: showConfirmationModal = JSON.stringify(clonedSceneData) !== JSON.stringify(sceneData);
</script>

<Modal
	title={$format(`scene_process_main_title`, {
		values: {
			process:
				sceneData.do_info.mode === 'edit'
					? capFirstChar($format('process_edit'))
					: $format('process_add'),
			type: $format('process_scene_action'),
		},
	})}
	showModal={showWizard}
	{backdropClose}
	on:close={showExitConfirmation}
	bind:this={modalRef}
	bind:scrollContainerRef={$containersRefsObj.scrollContainerRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={item.title}
				showMoreDots={item.showMoreDots}
				disabled={isWizardStepDisabled(item, i)}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<Container padding="0" slot="content">
		<WizardContainer
			activeStepIndex={activeWizardStep.activeIndex}
			adaptiveHeight
			bind:wizardItemsContainerRef
			bind:wizardContainerRef={$containersRefsObj.wizardContainerRef}
		>
			{#each wizardItems as _, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent
					active={activeWizardStep.activeIndex === i}
					class={classMap({
						'pb-s': true,
						'pt-s': sceneData.selectedAction !== 2,
					})}
				>
					{#if activeWizardStep.activeIndex === i}
						<!-- TODO FIX: was created with unknown prop 'firstSelectedRef' -->
						<svelte:component
							this={mappedWizardStepsComponents[sceneData.selectedAction][i]}
							bind:sceneData
							bind:firstSelectedRef
						/>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</Container>

	<div class="d-flex justify-content-end" slot="footer">
		<Button
			class={classMap({
				'width-md-m width-sm-full mr-s': true,
				'visibility-hidden': activeWizardStep.activeIndex === 0,
			})}
			icon="fas fa-chevron-left"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				onClickPrevNextButton('prev');
			}}
		>
			{previousButton}
		</Button>
		<Button
			class="width-md-m width-sm-full"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isNextButtonDisabled(sceneData, activeWizardStep)}
			on:click={() => {
				if (isLastStepWizard) {
					handleSave();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_action', { default: 'the action' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showWizard}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		dispatch('close');
	}}
/>

<!--
	FE-1682: surfaced when a notification action carries recipient emails that
	no longer match anyone in the active home. The author chooses to either
	purge them or keep them on the saved action.
-->
{#if showOrphanModal}
	<OrphanRecipientConfirmModal
		showModal={showOrphanModal}
		orphans={pendingOrphans}
		on:removeAndSave={onOrphanRemoveAndSave}
		on:keepAndSave={onOrphanKeepAndSave}
		on:cancel={onOrphanCancel}
	/>
{/if}
